import { useEffect, useState } from "react";
import { Download, Send, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, ts, dmy } from "@/lib/api";
import { Th, Td, btn } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";

export const asnToast = (d) => {
  const sent = d.deliveries.filter((x) => x.status === "sent").length;
  const bad = d.deliveries.filter((x) => x.status === "rejected").length;
  const msg = `${d.filename}: ${d.deliveries.length} delivery(ies) read - ${sent} sent, ${d.deliveries.length - sent - bad} to review, ${bad} rejected`;
  bad ? toast.warning(msg) : toast.success(msg);
};

const AutoSend = () => {
  const [on, setOn] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setOn(!!r.data.havi_asn_auto)); }, []);
  if (on === null) return null;
  const set = (v) => api.put("/settings", { havi_asn_auto: v }).then(() => { setOn(v); toast.success(v ? "856 sent straight away when a delivery fully matches" : "Deliveries wait for review before the 856 is sent"); }).catch((e) => toast.error(errMsg(e)));
  return (
    <label className="flex items-center gap-2 text-sm text-slate-300" data-testid="havi-asn-auto">
      <Switch checked={on} onCheckedChange={set} data-testid="havi-asn-auto-switch" /> Send the 856 straight away when every line matches (otherwise held for review)
    </label>
  );
};

const Lines = ({ a }) => (
  <div className="text-xs text-slate-400">
    {a.shipment.lines.map((l, i) => <div key={i} className="font-mono">L{l.line_no} {l.item} x {l.qty}{l.lot && ` lot ${l.lot}`}{l.sscc && ` SSCC ${l.sscc}`}{l.expiry_date && ` exp ${dmy(l.expiry_date)}`}</div>)}
  </div>
);

export const HaviDeliveries = ({ tick }) => {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/havi/asns").then((r) => setRows(r.data));
  useEffect(() => { load(); }, [tick]);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("kind", "asn");
    try { asnToast((await api.post("/havi/upload", fd)).data); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const tpl = () => api.get("/havi/template/asn", { responseType: "blob" }).then((r) => { const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "SAP_HAVI_Delivery_template.csv"; a.click(); });
  const send = (a) => api.post(`/havi/asns/${a.id}/send`).then((r) => { toast.success(`856 for PO ${a.po_number}: ${r.data.sent || "generated"}`); load(); }).catch((e) => toast.error(errMsg(e)));
  const del = (a) => window.confirm(`Delete delivery ${a.delivery_no}?`) && api.delete(`/havi/asns/${a.id}`).then(load).catch((e) => toast.error(errMsg(e)));
  return (
    <div data-testid="havi-deliveries">
      <div className="space-y-3 px-5 py-4">
        <p className="max-w-3xl text-sm text-slate-400">SAP delivery export (one row per pallet or lot): the Hub matches each delivery to the HAVI PO and line, fills the 856 ASN with lots, SSCCs and dates, then holds it for review or sends it. File names containing "Delivery" or "ASN" are picked up by the auto-upload script too.</p>
        <div className="flex flex-wrap items-center gap-3">
          <label className={`inline-flex h-9 cursor-pointer items-center rounded-md px-3 text-sm ${btn.ghost}`} data-testid="havi-asn-upload-label"><Upload className="mr-1.5 h-4 w-4" /> Upload SAP delivery file<input type="file" accept=".csv,.txt,.xlsx,.xls" className="hidden" onChange={upload} data-testid="havi-asn-upload-input" /></label>
          <Button variant="ghost" className={btn.ghost} onClick={tpl} data-testid="havi-asn-template"><Download className="mr-1.5 h-4 w-4" /> SAP delivery template</Button>
          <AutoSend />
        </div>
      </div>
      {rows === null ? <p className="px-5 pb-4 text-xs text-slate-500">Loading...</p> : !rows.length ? <p className="px-5 pb-4 text-xs text-slate-500" data-testid="havi-deliveries-empty">No SAP deliveries yet.</p> : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead className="border-b border-slate-800"><tr><Th>Read</Th><Th>Delivery</Th><Th>PO</Th><Th>Ship date</Th><Th>Pallets / lots</Th><Th>Status</Th><Th></Th></tr></thead>
          <tbody className="divide-y divide-slate-800/70">{rows.map((a) => (
            <tr key={a.id} data-testid={`havi-delivery-${a.id}`}>
              <Td className="whitespace-nowrap font-mono text-slate-500">{ts(a.created_at)}<div className="text-xs">{a.source_file}</div></Td>
              <Td className="font-mono">{a.delivery_no || "-"}</Td><Td className="font-mono">{a.po_number}</Td><Td>{dmy(a.shipment.ship_date)}</Td>
              <Td><Lines a={a} /></Td>
              <Td><StatusBadge status={a.status === "sent" ? "sent" : a.status === "rejected" ? "error" : "ready"} label={a.status === "rejected" ? `Rejected - ${a.errors.length} error(s)` : a.status === "review" ? "Ready to review" : `Sent ${a.sent_at ? ts(a.sent_at) : ""}`} />
                {a.errors?.length > 0 && <div className="mt-1 max-w-md text-xs text-rose-300" data-testid={`havi-delivery-errors-${a.id}`}>{a.errors.slice(0, 3).join("; ")}</div>}</Td>
              <Td className="whitespace-nowrap text-right">
                {a.status === "review" && <Button size="sm" variant="ghost" className={btn.ghost} onClick={() => send(a)} data-testid={`havi-delivery-send-${a.id}`}><Send className="mr-1 h-3.5 w-3.5" /> Send 856</Button>}
                {a.status !== "sent" && <Button size="sm" variant="ghost" className={btn.danger} onClick={() => del(a)} data-testid={`havi-delivery-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
              </Td>
            </tr>))}</tbody>
        </table></div>)}
    </div>
  );
};
